import offsetInterval from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/utils/offsetInterval";
import Checkbox from "components/checkbox/Checkbox";
import DatePicker from "components/date-picker/DatePicker";
import RequiredAsterisk from "components/required-asterisk";
import messages from "messages";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { Form } from "react-bootstrap";
import styles from "./AvailabilityScheduleRange.module.scss";

const DATE_FORMAT = "MM/dd/yyyy";

type Props = {
  timezone: TimeZone;
  period: ParsedAvailableTimePeriod;
  onChange: (arg: ParsedAvailableTimePeriod) => void;
};

const AvailabilityScheduleRange = ({ timezone, period, onChange }: Props) => {
  const weekStartDateValue = useMemo(
    () =>
      moment
        .tz(period.start_date || Date.now(), timezone)
        .startOf("week")
        .valueOf(),
    [period, timezone],
  );

  const startDate = useMemo(
    () =>
      !period.start_date
        ? null
        : moment.tz(period.start_date, timezone).startOf("day"),
    [period, timezone],
  );

  const endDate = useMemo(
    () =>
      !period.end_date
        ? null
        : moment.tz(period.end_date, timezone).endOf("day"),
    [period, timezone],
  );

  const minDate = useMemo(
    () => startDate || moment().tz(timezone).startOf("day"),
    [startDate, timezone],
  );

  const endDatePickerRef = useRef<HTMLFormElement | null>(null);

  const onStartDateChange = useCallback(
    (date: Date) => {
      // compare week start
      const days = moment(+date)
        .tz(timezone)
        .startOf("week")
        .diff(moment(weekStartDateValue).tz(timezone), "day");
      // migrate old schedule to the new week start
      if (!endDate) {
        const nextIntervals = period.intervals.map((el) =>
          el === null
            ? null
            : el.map((each) => offsetInterval(each, days, timezone)),
        );
        onChange({
          ...period,
          intervals: nextIntervals,
          start_date: (date as Date).toISOString(),
        });
        return;
      }

      const momentStartDate = moment(date).tz(timezone);
      const nextDayDiff = !date
        ? 7
        : Math.min(7, Math.max(0, endDate.diff(momentStartDate, "day") + 1));

      const weekDayIds = Array(nextDayDiff)
        .fill(0)
        .map((_, i) => momentStartDate.weekday() + i);

      const nextIntervals = period.intervals.map((el, i) =>
        weekDayIds.includes(i) && el
          ? el.map((each) => offsetInterval(each, days, timezone))
          : null,
      );

      onChange({
        ...period,
        intervals: nextIntervals,
        start_date: (date as Date).toISOString(),
      });
    },
    [endDate, onChange, timezone, weekStartDateValue, period],
  );

  const onEndDateChange = useCallback(
    (date: Date) => {
      const nextDayDiff = !date
        ? 7
        : Math.min(
            7,
            Math.max(0, moment(date).tz(timezone).diff(startDate, "day") + 1),
          );
      const weekDayIds = Array(nextDayDiff)
        .fill(0)
        .map((_, i) => ((startDate || minDate).weekday() + i) % 7);

      const nextIntervals = period.intervals.map((el, i) =>
        weekDayIds.includes(i) ? el : null,
      );

      onChange({
        ...period,
        intervals: nextIntervals,
        end_date: (date as Date).toISOString(),
      });
    },
    [onChange, startDate, timezone, minDate, period],
  );

  const onRecurringChange = useCallback(
    (e) => {
      onChange({
        ...period,
        end_date: e.target.checked ? null : period.end_date,
        recurring: e.target.checked,
      });
      endDatePickerRef.current?.reset();
    },
    [onChange, period],
  );

  return (
    <Form.Group controlId="dateRange" className={styles.container}>
      <Form.Label className={styles.title}>{messages.dateRange}</Form.Label>

      <div className={styles.row}>
        <label htmlFor="startDate" className={styles.label}>
          {messages.startDate}
          <RequiredAsterisk />
        </label>
        <DatePicker
          id="startDate"
          returnStartOfDay={true}
          timezone={timezone}
          selected={startDate?.toDate()}
          onChange={onStartDateChange}
          maxDate={endDate?.toDate()}
          placeholderText={messages.selectStartDate}
          required={true}
          validityMessage={messages.selectAStartDate}
          dateFormat={DATE_FORMAT}
        />
      </div>

      <div className={styles.row}>
        <label htmlFor="endDate" className={styles.label}>
          {messages.endDate}
          {!period.recurring && <RequiredAsterisk />}
        </label>
        <DatePicker
          ref={endDatePickerRef}
          id="endDate"
          timezone={timezone}
          selected={endDate?.toDate()}
          onChange={onEndDateChange}
          minDate={minDate.toDate()}
          placeholderText={messages.selectEndDate}
          disabled={period.recurring}
          required={!period.recurring}
          validityMessage={messages.selectAnEndDate}
          dateFormat={DATE_FORMAT}
        />
      </div>
      <Checkbox
        variant="rectangle"
        label={messages.noEndDate}
        checked={period.recurring}
        onChange={onRecurringChange}
      />
    </Form.Group>
  );
};

export default AvailabilityScheduleRange;
